/**
 * Which currency the prices on this deployment are in.
 *
 * Every amount is already FORMATTED from `NUXT_PUBLIC_BILLING_CURRENCY` /
 * `NUXT_PUBLIC_BILLING_LOCALE` (see `useBillingStore().formatMicroCents`), so
 * the number and its symbol are always right. What was missing is that a
 * SYMBOL is not an identity: `$` is the dollar of a dozen countries, and a
 * customer reading "$ 73.30" has no way to tell which one they are about to be
 * charged in. That is not hypothetical — the platform is whitelabel and the
 * currency is a deployment setting.
 *
 * So the code is stated in words next to the prices. The NAME is resolved in
 * the language the console is being read in, not in the billing locale: a
 * currency's name is prose, and "Brazilian real" is what makes BRL mean
 * something to someone who has never seen it.
 */
export function useBillingCurrency() {
  const { billingCurrency } = useRuntimeConfig().public
  const { locale } = useI18n()

  const code = computed(() => String(billingCurrency || 'BRL').toUpperCase())

  const name = computed(() => {
    try {
      // Older engines have no Intl.DisplayNames, and `.of()` answers with the
      // code itself for a currency it does not know. Either way the note still
      // names the currency — it just does not translate it.
      return (
        new Intl.DisplayNames([locale.value], { type: 'currency' }).of(
          code.value
        ) ?? code.value
      )
    } catch {
      return code.value
    }
  })

  return { code, name }
}
